import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Truck, MapPin, Coffee, Clock, RefreshCw, 
  Circle, Navigation, CheckCircle2, Timer
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { id: 'on_route', label: 'Start Route', icon: Truck, color: 'bg-green-600 hover:bg-green-700', activeColor: 'bg-green-100 text-green-700 border-green-300', needsInput: true },
  { id: 'at_store', label: 'At Store', icon: MapPin, color: 'bg-blue-600 hover:bg-blue-700', activeColor: 'bg-blue-100 text-blue-700 border-blue-300', needsInput: true },
  { id: 'on_break', label: 'On Break', icon: Coffee, color: 'bg-yellow-600 hover:bg-yellow-700', activeColor: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { id: 'returning', label: 'Returning', icon: Navigation, color: 'bg-purple-600 hover:bg-purple-700', activeColor: 'bg-purple-100 text-purple-700 border-purple-300' },
  { id: 'off_duty', label: 'End Shift', icon: Circle, color: 'bg-gray-600 hover:bg-gray-700', activeColor: 'bg-gray-100 text-gray-700 border-gray-300' },
];

// Common store names for quick selection
const STORE_OPTIONS = [
  'Hoppers Crossing',
  'Melton',
  'Craigieburn',
  'Clyde',
  'Auburn Store',
  'Warehouse'
];

export default function DriverStatusWidget() {
  const [currentStatus, setCurrentStatus] = useState('off_duty');
  const [currentStore, setCurrentStore] = useState('');
  const [currentEta, setCurrentEta] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [etaMinutes, setEtaMinutes] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showInput, setShowInput] = useState(null); // 'on_route' or 'at_store'

  const fetchMyStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/driver-tracking/my-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentStatus(data.current_status || 'off_duty');
        setCurrentStore(data.current_store || '');
        setCurrentEta(data.eta_timestamp || null);
        setActivities(data.today_activities || []);
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyStatus();
    const interval = setInterval(fetchMyStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchMyStatus]);

  const updateStatus = async (newStatus, store = null, eta = null) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/driver-tracking/update-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          store_name: store || null,
          eta_minutes: eta ? parseInt(eta) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentStatus(newStatus);
        if (store) {
          setCurrentStore(store);
        } else if (newStatus !== 'at_store' && newStatus !== 'on_route') {
          setCurrentStore('');
        }
        setStoreName('');
        setEtaMinutes('');
        setShowInput(null);
        toast.success(data.message || 'Status updated');
        fetchMyStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusClick = (statusId) => {
    const option = STATUS_OPTIONS.find(s => s.id === statusId);
    if (option?.needsInput) {
      setShowInput(statusId);
      setStoreName('');
      setEtaMinutes('');
    } else {
      updateStatus(statusId);
    }
  };

  const handleInputSubmit = (e) => {
    e.preventDefault();
    if (storeName.trim()) {
      updateStatus(showInput, storeName, showInput === 'on_route' ? etaMinutes : null);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-AU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getEtaDisplay = () => {
    if (!currentEta) return null;
    const eta = new Date(currentEta);
    const now = new Date();
    const diffMs = eta - now;
    if (diffMs <= 0) return 'Arriving now';
    const diffMins = Math.ceil(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const currentStatusConfig = STATUS_OPTIONS.find(s => s.id === currentStatus);
  const CurrentIcon = currentStatusConfig?.icon || Circle;

  if (loading) {
    return (
      <Card className="h-full" data-testid="driver-status-widget">
        <CardContent className="flex items-center justify-center h-48">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden" data-testid="driver-status-widget">
      <CardHeader className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            <CardTitle className="text-base font-bold">My Status</CardTitle>
          </div>
          <Badge className={currentStatusConfig?.activeColor || 'bg-gray-100'}>
            <CurrentIcon className="h-3 w-3 mr-1" />
            {currentStatusConfig?.label?.replace('Start ', '').replace('End ', '') || 'Off Duty'}
          </Badge>
        </div>
        {currentStore && (
          <p className="text-green-100 text-xs mt-1 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {currentStore}
            {currentStatus === 'on_route' && currentEta && (
              <span className="ml-2 flex items-center gap-1">
                <Timer className="h-3 w-3" /> ETA: {getEtaDisplay()}
              </span>
            )}
          </p>
        )}
      </CardHeader>

      <CardContent className="p-3 space-y-4">
        {/* Input Panel for Start Route or At Store */}
        {showInput && (
          <div className={`p-3 rounded-lg border ${showInput === 'on_route' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
            <p className={`text-sm font-medium mb-2 ${showInput === 'on_route' ? 'text-green-800' : 'text-blue-800'}`}>
              {showInput === 'on_route' ? 'Where are you heading?' : 'Which store are you at?'}
            </p>
            <form onSubmit={handleInputSubmit} className="space-y-2">
              {/* Quick store selection */}
              <div className="flex flex-wrap gap-1 mb-2">
                {STORE_OPTIONS.map(store => (
                  <button
                    key={store}
                    type="button"
                    onClick={() => setStoreName(store)}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      storeName === store 
                        ? (showInput === 'on_route' ? 'bg-green-600 text-white border-green-600' : 'bg-blue-600 text-white border-blue-600')
                        : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    {store}
                  </button>
                ))}
              </div>
              
              <Input
                placeholder="Or type store name..."
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                className="text-sm"
              />
              
              {/* ETA input only for Start Route */}
              {showInput === 'on_route' && (
                <div className="flex items-center gap-2">
                  <Timer className="h-4 w-4 text-green-600" />
                  <Input
                    type="number"
                    placeholder="ETA (minutes)"
                    value={etaMinutes}
                    onChange={(e) => setEtaMinutes(e.target.value)}
                    className="text-sm flex-1"
                    min="1"
                    max="300"
                  />
                  <span className="text-xs text-slate-500">mins</span>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  size="sm"
                  disabled={!storeName.trim() || updating}
                  className={showInput === 'on_route' ? 'bg-green-600 hover:bg-green-700 flex-1' : 'bg-blue-600 hover:bg-blue-700 flex-1'}
                >
                  {updating ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  {showInput === 'on_route' ? 'Start Route' : 'Confirm'}
                </Button>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowInput(null)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Quick Status Buttons */}
        {!showInput && (
          <div className="grid grid-cols-2 gap-2">
            {STATUS_OPTIONS.map(option => {
              const Icon = option.icon;
              const isActive = currentStatus === option.id;
              return (
                <Button
                  key={option.id}
                  onClick={() => handleStatusClick(option.id)}
                  disabled={updating || isActive}
                  className={`h-auto py-3 flex flex-col items-center gap-1 ${
                    isActive 
                      ? `${option.activeColor} border-2` 
                      : `${option.color} text-white`
                  }`}
                  variant={isActive ? 'outline' : 'default'}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs">{option.label}</span>
                </Button>
              );
            })}
          </div>
        )}

        {/* Today's Activity */}
        <div>
          <h4 className="text-xs font-medium text-slate-500 mb-2">Today's Activity</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {activities.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-2">No activity today</p>
            ) : (
              activities.slice(0, 5).map((activity, idx) => {
                const StatusIcon = STATUS_OPTIONS.find(s => s.id === activity.status)?.icon || Circle;
                return (
                  <div 
                    key={activity.id || idx}
                    className="flex items-center gap-2 text-xs text-slate-600 py-1 border-b border-slate-100 last:border-0"
                  >
                    <span className="text-slate-400 w-12">{formatTime(activity.timestamp)}</span>
                    <StatusIcon className="h-3 w-3" />
                    <span>{activity.status_label}</span>
                    {activity.store_name && (
                      <span className="text-slate-400">• {activity.store_name}</span>
                    )}
                    {activity.eta_minutes && (
                      <span className="text-green-600 text-[10px]">({activity.eta_minutes}min ETA)</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
